import { useCallback, useEffect, useRef } from 'react';
import { useFetcher } from '@remix-run/react';
import type { loader } from '~/routes/actions.search.aggregate';
import { useLatest } from 'ahooks';
import { SEARCH_AGGREGATE_API } from '@modules-rocco/search/config/search-service.config';
import type { FilterStates } from '@modules-rocco/filters/types/filter-states';
import type { SearchIndexes } from '@modules-rocco/search/types/enum/search-indexes';
import { mapFilterStatesToQueryAggregations } from '@modules-rocco/search/data-mappers/filter-states-to-query-aggregations';
import { mapFilterStatesToURL } from '@modules-rocco/filters/data-mappers/filter-states-to-url';
import {
    type AggregationArgs,
    isFilterAggregation,
    isSearchableListAggregation,
} from '@rocco/ui/filter-sort/hooks/use-search-filters';
import { parseURL } from '@modules-rocco/url/api/parse-url';

// ▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄
// General interfaces for this hook
// ▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀

type AggregateState = 'wait' | 'loading' | 'loaded' | 'cancel';

interface AggregateRequest {
    args: AggregationArgs;
    onAggregateStateChange: (
        state: AggregateState,
        data?: FilterStates
    ) => void;
}

// ▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄
// `useAggregation` hook
// ▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀

interface UseAggregationProps {
    searchIndex?: SearchIndexes;
}

/**
 * This hook is used to fetch the Search Service aggregation result based on filter states and params
 */
export const useAggregation = (props: UseAggregationProps) => {
    const { searchIndex } = props;
    const fetcher = useFetcher<typeof loader>();
    const aggregateFetcher = useLatest(fetcher);
    const requests = useRef<AggregateRequest[]>([]);
    const currentRequest = useRef<AggregateRequest | null>(null);

    const cancelRequest = useCallback((request: AggregateRequest) => {
        request.onAggregateStateChange('cancel');

        // check if it's a current request - we need to cancel fetcher
        if (currentRequest.current === request) {
            currentRequest.current = null;
        }

        // remove from all requests
        const allRequests = requests.current;
        const index = allRequests.indexOf(request);
        if (index > -1) {
            allRequests.splice(index, 1);
        }
    }, []);

    const executeAggregate = useCallback(() => {
        const allRequests = requests.current;

        if (
            aggregateFetcher.current.state !== 'idle' ||
            !allRequests.length ||
            currentRequest.current
        ) {
            return;
        }

        const request = allRequests.shift();

        if (!request) {
            executeAggregate();
            return;
        }

        currentRequest.current = request;

        const aggregationParams = isFilterAggregation(request.args)
            ? mapFilterStatesToQueryAggregations(
                  request.args.filterStates,
                  searchIndex
              )
            : request.args.aggregationsParams;

        if (!aggregationParams) {
            cancelRequest(request);
            executeAggregate();
            return;
        }

        // if we have FilterAggregateRequest or grouping options then we need to build new URL with filters included
        // as in modal filters we don't change current URL right after adding / removing a filter value
        // for other cases it should be safe to use current URL
        const currentURL =
            location.pathname.replace('/suppliers', '') + location.search;
        const urlInfo = parseURL(
            request.args.filterStates
                ? mapFilterStatesToURL(request.args.filterStates, currentURL)
                : currentURL
        );

        // TODO: get sort?
        // TODO: do we need explicitly add categoryLink here (e.g. like the old web)? why?

        request.onAggregateStateChange('loading');
        aggregateFetcher.current.load(
            `${SEARCH_AGGREGATE_API}?index=${searchIndex}&url=${encodeURIComponent(
                JSON.stringify(urlInfo)
            )}&aggregationQuery=${encodeURIComponent(
                JSON.stringify(aggregationParams)
            )}`
        );
    }, [aggregateFetcher, searchIndex, cancelRequest]);

    const handleAggregate = useCallback(
        (args: AggregationArgs) => {
            const allRequests = requests.current;

            if (isSearchableListAggregation(args)) {
                const duplicateIndex = allRequests.findIndex(
                    (queueRequest) =>
                        queueRequest.args.aggregateType ===
                            args.aggregateType &&
                        queueRequest.args.aggregateFilter ===
                            args.aggregateFilter
                );

                if (duplicateIndex !== -1) {
                    cancelRequest(allRequests[duplicateIndex]!);
                }
            }

            // There's should be only one filter aggregation running at the time
            if (isFilterAggregation(args)) {
                // check current running filter aggregation & cancel it
                if (
                    currentRequest.current?.args.aggregateType ===
                    args.aggregateType
                ) {
                    cancelRequest(currentRequest.current);
                }

                // Find all duplicate filter aggregations with the same filter group
                const duplicateIndices = allRequests
                    .map((request, index) => ({ request, index }))
                    .filter(
                        ({ request }) =>
                            request.args.aggregateType === args.aggregateType
                    );

                // Cancel and remove all duplicates
                if (duplicateIndices.length > 0) {
                    duplicateIndices.forEach(({ request }) => {
                        cancelRequest(request);
                    });
                }
            }

            const newRequest: AggregateRequest = {
                args,
                onAggregateStateChange: (state, data) => {
                    if (state === 'wait') {
                        args.onStart();
                    }

                    if (state === 'loaded') {
                        data && args.onSuccess(data);
                        args.onFinish();
                    }

                    if (state === 'cancel') {
                        args.onFinish();
                    }
                },
            };

            allRequests.push(newRequest);
            newRequest.onAggregateStateChange('wait');
            executeAggregate();
        },
        [cancelRequest, executeAggregate]
    );

    const resetAggregationQueue = useCallback(() => {
        if (currentRequest.current) {
            cancelRequest(currentRequest.current);
        }

        requests.current.forEach((request) => {
            cancelRequest(request);
        });
    }, [cancelRequest]);

    useEffect(() => {
        if (
            aggregateFetcher.current.state !== 'idle' ||
            !currentRequest.current
        ) {
            return;
        }

        const request = currentRequest.current;
        const data = aggregateFetcher.current.data;

        request.onAggregateStateChange('loaded', data ?? undefined);
        currentRequest.current = null;

        if (requests.current.length) {
            executeAggregate();
        }
    }, [
        aggregateFetcher,
        aggregateFetcher.current.state,
        aggregateFetcher.current.data,
        currentRequest,
        executeAggregate,
    ]);

    return {
        handleAggregate,
        resetAggregationQueue,
        loading: fetcher.state === 'loading',
    };
};
